<template>
  <span class="donut-chart-multi-value">
    <svg class="donut-svg" :viewBox="`0 0 ${canvasSize} ${canvasSize}`">
      <DonutSvgBackground
        :center-x="centerPoint"
        :center-y="centerPoint"
        :radius="radius"
        :stroke-width="strokeWidth"
      />
      <DonutSvgFill
        v-for="({ key, color, value }, index) in manipulatedValues"
        :key="key"
        :value="value"
        :color="color"
        :center-x="centerPoint"
        :center-y="centerPoint"
        :radius="radius"
        :stroke-width="strokeWidth"
        :transition-duration="duration"
        :dash-array="getDashArray(value)"
        :dash-offset="getDashOffset(index)"
      />
    </svg>
    <span v-if="!hideValue" class="donut-value"
      ><span class="display-value"
        ><AnimatedNumber
          :value="isMounted ? displayValue : 0"
          :duration="duration"
          :decimals="decimals"
          ease="out"
        />{{ unit }}</span
      ><span v-if="text || $slots.default" class="donut-text">
        <slot />{{ text }}
      </span>
    </span>
  </span>
</template>

<script>
import AnimatedNumber from '../ui/AnimatedNumber'
import DonutSvgBackground from '@/components/charts/DonutSvgBackground'
import DonutSvgFill from '@/components/charts/DonutSvgFill'

import { SECOND } from '@/utils/date'
import { hasProperty } from '@/utils/object'
import { valueToDashArray, valueToDashSize } from '@/utils/svg'
import { getCircleCircumference } from '@/utils/math'

/**
 * A donut chart for displaying multiple values stacked around the edge of a donut.
 */
export default {
  name: 'DonutChartMultiValue',
  components: { AnimatedNumber, DonutSvgBackground, DonutSvgFill },
  props: {
    /**
     * An array of values, their colors and keys.
     */
    values: {
      type: Array,
      required: true,
      validator: array => {
        return !array.some(
          value =>
            !hasProperty(value, 'key') ||
            !hasProperty(value, 'value') ||
            !hasProperty(value, 'color')
        )
      },
    },
    /**
     * The value to display at the center of the chart
     */
    displayValue: {
      type: Number,
      required: false,
    },
    max: {
      type: Number,
      required: true,
    },
    decimals: {
      type: Number,
      required: false,
      default: 0,
    },
    unit: {
      type: String,
      required: false,
      default: '',
    },
    text: {
      type: String,
      required: false,
      default: '',
    },
    duration: {
      type: Number,
      required: false,
      default: 0.5 * SECOND,
    },
    strokeWidth: {
      type: Number,
      required: false,
      default: 8,
    },
  },
  data() {
    return { canvasSize: 100, isMounted: false }
  },
  computed: {
    /**
     * When mounting the component, all values are returned as 0.
     * Once mounted, the values will be updated to their proper values to create the initial animation.
     * @return {Array}
     */
    manipulatedValues() {
      if (!this.isMounted) {
        return this.values.map(value => ({ ...value, value: 0 }))
      }

      return this.values
    },

    /**
     * Returns true if the value at the center of the chart should be hidden.
     * @return {boolean}
     */
    hideValue() {
      return typeof this.displayValue !== 'number'
    },

    /**
     * @return {number}
     */
    radius() {
      return (this.canvasSize - this.strokeWidth) / 2
    },

    /**
     * @return {number}
     */
    centerPoint() {
      return this.canvasSize / 2
    },

    /**
     * @return {number}
     */
    circumference() {
      return getCircleCircumference(this.radius)
    },
  },
  methods: {
    /**
     * Returns the dash array for the given value.
     * @param {number} value
     * @return {string}
     */
    getDashArray(value) {
      return valueToDashArray(value, this.max, this.circumference)
    },

    /**
     * Returns the dash offset for the given index.
     * @param {number} targetIndex
     * @return {number}
     */
    getDashOffset(targetIndex) {
      // Calculate the sum of all values positioned before the value with the given index
      const sum = this.manipulatedValues.reduce(
        (previous, { value: current }, index) => {
          if (index < targetIndex) {
            return previous + current
          }

          return previous
        },
        0
      )

      // Invert the value and return
      return 0 - valueToDashSize(sum, this.max, this.circumference)
    },
  },
  mounted() {
    // Update the isMounted state to start the initial animation
    setTimeout(() => {
      this.isMounted = true
    })
  },
}
</script>

<style lang="scss" scoped>
.donut-chart-multi-value {
  --size: 8em;
  --chart-color: currentColor;

  position: relative;
  display: inline-block;
  width: var(--size);
}

.donut-svg {
  display: block;
  color: var(--chart-color);
}

.donut-value {
  position: absolute;
  display: block;
  left: 50%;
  top: 50%;
  width: 75%;
  text-align: center;
  transform: translate(-50%, -50%);
}

.display-value {
  font-size: calc(var(--size) / 3.25);
}

.donut-text {
  display: block;
  font-size: calc(var(--size) / 6);
  margin-top: -0.15em;
}
</style>
